import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { Badge } from "@/shared/ui/badge";

import { promotionStatusIntent } from "../lib/status";

type Rollup = components["schemas"]["CampaignStatusRollup"];

/**
 * Status codes shown as count pills, in mockup display order
 * (green active · blue finished · gray draft). `archived` is carried by the
 * rollup but, per the mockup, is not surfaced as a pill.
 */
const ORDER = ["active", "finish", "draft"] as const;
type RollupKey = (typeof ORDER)[number];

/**
 * Renders `● 11 active · 0 finish · 2 draft` style count pills from a campaign's
 * `status_rollup`. Zero-count statuses are omitted; when every count is zero the
 * mockup shows an em-dash (not a "0 draft" pill), so we render a muted "—".
 */
export function StatusCountPills({ rollup }: { rollup: Rollup }): JSX.Element {
  const { t } = useTranslation("campaign");
  const keys: RollupKey[] = ORDER.filter((k) => rollup[k] > 0);

  if (keys.length === 0) {
    return (
      <span className="text-[var(--text-subtle)]" data-testid="status-count-pills-empty">
        —
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="status-count-pills">
      {keys.map((k) => (
        <Badge
          key={k}
          dot
          intent={promotionStatusIntent(k)}
          className="tabular-nums"
          data-testid={`rollup-${k}`}
          data-count={rollup[k]}
        >
          <span className="font-bold">{rollup[k]}</span> {t(`rollup.${k}`).toLowerCase()}
        </Badge>
      ))}
    </div>
  );
}
